/* The comparison table, built from the specification layer.

   Phase 2 of the pending-specs handoff. Until now the six pair pages carried
   their figures as literals in the markup — 693 x 341 x 410 mm typed into the
   DELTA Pro 3 page — which works for six pairs chosen by hand and not for the
   378 a 28-product catalogue contains.

   The row schema is NOT written here. It ships in the payload, so the labels,
   units and group rules are defined once in specs.py and read twice. The only
   thing written on both sides is the handful of derived helpers below, and each
   is checked against the Python output for the same pair.

   The six written pages keep their URLs and their verdicts. Picking a pair that
   has one navigates there, because a verdict is editorial and this cannot write
   one; picking any other pair renders the table in place, which is the part the
   data can answer.
*/
(function () {
  'use strict';

  var node = document.getElementById('ef-specs');
  if (!node) return;
  var DATA;
  try { DATA = JSON.parse(node.textContent); } catch (e) { return; }

  var BY_ID = {};
  DATA.products.forEach(function (p) { BY_ID[p.id] = p; });

  /* Two id spaces meet on this page. The records key on their own id
     ("delta-pro"); the catalogue, the catpick buttons and the written-pair keys
     all speak the DESIGN id ("pro"), and for 22 of 28 products those differ.
     A pair arriving from a catalogue Compare button is therefore in the design
     space, and looking it up in BY_ID alone silently finds nothing — the page
     then quietly opens on its default pair instead of the one that was asked
     for. Resolve through both, record id first. */
  var BY_DESIGN = {};
  DATA.products.forEach(function (p) {
    if (p.design_id) BY_DESIGN[p.design_id] = p;
  });
  function resolve(id) {
    if (!id) return null;
    var p = BY_ID[id] || BY_DESIGN[id];
    return p ? p.id : null;
  }

  var DASH = '—';
  var NOT_APPLICABLE = 'Not applicable';

  /* ---- derived helpers. Twins of the Python; same guards, same rounding. ---- */
  function n(v) { return (typeof v === 'number' && v % 1 === 0) ? v : v; }

  function group(v) {
    /* No thousands separators in a table cell. EcoFlow's rule is "numbers are
       always numerals, units set tight" and the six written comparison pages
       print 4096Wh, so a generated table that printed "4,096 Wh" beside them
       would read as the odd one out. The catalogue cards use the other
       convention; they are rendered elsewhere. */
    return String(v);
  }

  function kwh(v, unit) {
    /* Twin of the Python. Big energy figures read as kWh — 'Expandable to
       12kWh' beside 'Capacity 4096Wh' is the written pages' own habit. */
    if (unit !== 'Wh' || typeof v !== 'number' || v < 10000) return null;
    var k = v / 1000;
    return (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)) + 'kWh';
  }

  function fmt(v, unit) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') {
      var big = kwh(v, unit);
      if (big) return big;
      return group(n(v)) + (unit || '');
    }
    return String(v) + (unit || '');
  }

  function footprint(d) { return d ? (d.w * d.d) / 100 : null; }
  function volume(d) { return d ? (d.w * d.d * d.h) / 1000000 : null; }

  function density(wh, kg) {
    /* Guarded, like every other division here: the current build can print
       "RM Infinity / Wh" because nothing checks the denominator. */
    return (wh && kg && kg > 0) ? wh / kg : null;
  }
  function perWh(price, wh) { return (price != null && wh && wh > 0) ? price / wh : null; }
  function perW(price, w) { return (price != null && w && w > 0) ? price / w : null; }

  function valueMetric(r) {
    if (r.group === 'ac-power-station' || r.group === 'dc-power-station') {
      var a = perWh(r.price_myr, r.capacity_wh);
      return a ? 'RM ' + a.toFixed(2) + ' / Wh' : null;
    }
    if (r.group === 'solar-panel') {
      var b = perW(r.price_myr, r.rated_w);
      return b ? 'RM ' + b.toFixed(2) + ' / W' : null;
    }
    return null;   /* an appliance has no capacity to divide by */
  }

  function derived(name, r) {
    var d = r.dimensions_wdh, v;
    if (name === 'dimensions') {
      if (d) return n(d.w) + ' × ' + n(d.d) + ' × ' + n(d.h) + ' mm';
      if (r.published_dimensions) {
        return r.published_dimensions + ' (' + (r.published_dimension_order || 'as published') + ')';
      }
      return null;
    }
    if (name === 'footprint') { v = footprint(d); return v ? Math.round(v) + ' cm²' : null; }
    if (name === 'volume') { v = volume(d); return v ? v.toFixed(1) + ' L' : null; }
    if (name === 'density') { v = density(r.capacity_wh, r.weight_kg); return v ? Math.round(v) + ' Wh/kg' : null; }
    if (name === 'ports') {
      var bits = [];
      [['USB-A', r.usb_a], ['USB-C', r.usb_c]].forEach(function (pair) {
        (pair[1] || []).forEach(function (x) {
          bits.push(x.count + 'x ' + pair[0] + ' ' + x.max_w_each + ' W');
        });
      });
      bits = bits.concat(r.dc_outputs || []);
      return bits.length ? bits.join('; ') : null;
    }
    if (name === 'grid') return r.grid_specification || DATA.grid_default;
    if (name === 'value') return valueMetric(r);
    return null;
  }

  var EXPANSION = ['expandable_to_wh', 'ecosystem_max_wh'];

  function cell(row, r) {
    if (row.derived) return derived(row.derived, r);
    /* Twin of the Python: a unit that takes no extra battery has no ceiling to
       publish, so it says so rather than showing an em dash that would read as
       "not published yet". */
    if (EXPANSION.indexOf(row.field) !== -1 && r.expandable_supported === false) {
      return 'Not supported';
    }
    return fmt(r[row.field], row.unit);
  }

  function applies(groups, g) { return !groups || groups.indexOf(g) !== -1; }

  function build(a, b) {
    if (!a || !b || a.id === b.id) return null;
    var cross = DATA.cross_ok;
    if (a.group !== b.group &&
        !(cross.indexOf(a.group) !== -1 && cross.indexOf(b.group) !== -1)) return null;

    var rows = [];
    DATA.rows.forEach(function (row) {
      var pa = applies(row.groups, a.group), pb = applies(row.groups, b.group);
      if (!pa && !pb) return;                       /* not a row at all */
      var va = pa ? cell(row, a) : NOT_APPLICABLE;
      var vb = pb ? cell(row, b) : NOT_APPLICABLE;
      if (va === null) va = DASH;
      if (vb === null) vb = DASH;
      if (va === DASH && vb === DASH) return;       /* neither publishes it */
      rows.push({ label: row.label, a: va, b: vb, section: row.section });
    });
    /* Same band order as the Python, taken from the payload rather than
       restated here — ROWS is authored by subject, so walking it in source
       order emits a band heading twice with another band in between. */
    var order = DATA.section_order || [];
    rows.sort(function (x, y) {
      var i = order.indexOf(x.section), j = order.indexOf(y.section);
      return (i < 0 ? 99 : i) - (j < 0 ? 99 : j);
    });
    return rows;
  }

  /* ------------------------------------------------------------- rendering */
  /* The designed comparison, not a table.
  
     The first version of this drew a plain <table>. Beside the six written
     pages — banded into "Power delivery" and "Charging", figures at 20px with a
     proportional bar under them and a ratio badge on the winner — it read as a
     different site, and it was removed for that. This renders the SAME shape
     from the same records, so a pair nobody wrote looks like a pair somebody
     did. The band a row belongs to travels in the payload beside its label, so
     the grouping is data, not a second opinion held here. */

  var ROW_GRID = 'display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,1fr) minmax(0,1fr);'
               + 'gap:24px;padding:22px 0;border-top:1px solid var(--ef-slate-200);';

  function el(tag, css, text) {
    var n = document.createElement(tag);
    if (css) n.setAttribute('style', css);
    if (text != null) n.textContent = text;
    return n;
  }

  function numOf(v) {
    /* The leading number, for the bar and the badge. "<20ms" and "2,048Wh"
       both have one; "LFP" and "Not supported" do not, and those rows simply
       get no bar rather than a bar of zero — an empty rail reads as a measured
       nothing. */
    if (typeof v !== 'string') return null;
    var m = v.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  function ratio(a, b) {
    if (!a || !b || a <= 0 || b <= 0) return null;
    var hi = Math.max(a, b), lo = Math.min(a, b), r = hi / lo;
    if (r < 1.02) return null;              /* not a difference worth marking */
    return r >= 1.5 ? r.toFixed(1) + '\u00d7' : '+' + Math.round((r - 1) * 100) + '%';
  }

  function cellNode(value, other, isBigger) {
    var wrap = el('div', '');
    var line = el('div', 'display:flex;align-items:baseline;gap:10px;');
    var strong = isBigger === true;
    line.appendChild(el('p',
      'margin:0;font-size:20px;line-height:1.2;font-variant-numeric:tabular-nums;'
      + 'color:' + (strong ? 'var(--ef-black)' : 'var(--ef-slate-600)') + ';'
      + 'font-weight:' + (strong ? '500' : '400') + ';', value));
    if (strong) {
      var badge = ratio(numOf(value), numOf(other));
      if (badge) {
        line.appendChild(el('span',
          'font-family:var(--ef-font-mono);font-size:12px;padding:2px 8px;'
          + 'background:var(--ef-blue-600);color:var(--ef-white);', badge));
      }
    }
    wrap.appendChild(line);
    var a = numOf(value), b = numOf(other);
    if (a !== null && b !== null && Math.max(a, b) > 0) {
      var rail = el('div', 'height:6px;margin-top:12px;background:var(--ef-slate-100);');
      var pct = Math.max(2, Math.round(100 * a / Math.max(a, b)));
      rail.appendChild(el('span',
        'display:block;height:6px;width:' + pct + '%;'
        + 'background:' + (strong ? 'var(--ef-blue-600)' : 'var(--ef-slate-300)') + ';'
        + 'transition:width 800ms var(--ef-ease-apple);'));
      wrap.appendChild(rail);
    }
    return wrap;
  }

  /* Rows whose bigger number is the WORSE one. Weight, footprint, volume and
     switchover all reward the smaller figure, and a bar that grew for those
     would recommend the heavier unit. */
  var LOWER_WINS = ['Weight', 'Footprint', 'Volume', 'Switchover',
                    'Wall charge time', 'Noise', 'Value'];

  function render(mount, a, b) {
    var rows = build(a, b);
    mount.replaceChildren();
    if (!rows) return false;

    var head = el('div', ROW_GRID + 'border-top:0;padding-top:0;');
    head.appendChild(el('p',
      'margin:0;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;'
      + 'color:var(--ef-slate-500);', 'Specification'));
    head.appendChild(el('p', 'margin:0;font-size:16px;font-weight:500;', a.title));
    head.appendChild(el('p', 'margin:0;font-size:16px;font-weight:500;', b.title));
    mount.appendChild(head);

    var band = null;
    rows.forEach(function (r) {
      if (r.section && r.section !== band) {
        band = r.section;
        var h = el('h3', 'margin:56px 0 0;font-size:28px;line-height:1.15;font-weight:500;', band);
        mount.appendChild(h);
      }
      var row = el('div', ROW_GRID);
      row.appendChild(el('p', 'margin:0;font-size:16px;font-weight:500;', r.label));
      var na = numOf(r.a), nb = numOf(r.b);
      var lower = LOWER_WINS.indexOf(r.label.split(' (')[0]) !== -1;
      var aWins = null, bWins = null;
      if (na !== null && nb !== null && na !== nb) {
        aWins = lower ? na < nb : na > nb;
        bWins = !aWins;
      }
      row.appendChild(cellNode(r.a, r.b, aWins));
      row.appendChild(cellNode(r.b, r.a, bWins));
      mount.appendChild(row);
    });
    return true;
  }

  /* -------------------------------------------------------------- wiring */
  function init(root) {
    var scope = root || document;
    var mount = scope.querySelector('[data-cmp-table]');
    var a = scope.querySelector('#cmp-a'), b = scope.querySelector('#cmp-b');
    if (!mount || !a || !b) return;

    /* The six written pages. Read from the catalogue payload ef-shop.js already
       ships, rather than through a global — one source, no coupling between the
       two scripts. When the chosen pair has a written page that page wins: it
       carries a verdict this cannot write, and it is the URL that ranks. */
    /* Captured before anything is drawn, so returning to the page's own pair
       can put its designed heading back verbatim. */
    /* The hub's <h1> is the HUB's name and stays that way. It used to be
       rewritten to the chosen pair, which read well but made the server-rendered
       page a near-duplicate of /pages/delta-pro-3-vs-delta-3-max-plus — measured
       at 99% identical body text, two URLs competing for one query. The pair now
       goes in its own line beneath, so the reader still sees what they picked
       and the written pair pages keep the pair terms to themselves. */
    var HEAD = mount.hasAttribute('data-cmp-hub')
      ? scope.querySelector('[data-cmp-pairname]') : null;
    var HEAD_TEXT = HEAD ? HEAD.textContent : '';
    function short(p) {
      return p ? p.title.replace(/^EcoFlow /, '') : '';
    }

    /* True once the reader has actually chosen a pair, as opposed to the page
       drawing its own default on load. Arriving with ?a=&b= counts: the reader
       chose that pair somewhere else and the address should survive a reload. */
    var userPicked = new URLSearchParams(location.search).has('a');

    /* The written verdict cards. Shown only when the reader is looking at the
       pair they were written about; hidden otherwise, because two cards arguing
       a different comparison read as the answer to the one just chosen. */
    var EXAMPLE = scope.querySelector('[data-cmp-example]');

    function groupOf(sel) {
      var o = sel.selectedOptions && sel.selectedOptions[0];
      return (o && o.parentElement && o.parentElement.label) || '';
    }

    /* Group labels are plural headings, and a naive s-strip writes "a air
       conditioning". Named singulars instead, with the article chosen from the
       word rather than assumed. Copy the reader sees is worth the six lines. */
    var NOUN = {
      'Power stations':    'power station',
      'DC power stations': 'DC power station',
      'Solar panels':      'solar panel',
      'Fridge freezers':   'fridge freezer',
      'Vehicle chargers':  'vehicle charger',
      'Air conditioning':  'air conditioner'
    };
    function noun(label) {
      return NOUN[label] || label.toLowerCase().replace(/s$/, '');
    }
    function withArticle(word) {
      return (/^[aeiou]/i.test(word) ? 'an ' : 'a ') + word;
    }

    /* Say why, in the reader's terms. A vanished table is indistinguishable
       from a broken page, and 204 of the 378 possible pairs land here. */
    function emptyState(A, B) {
      var ga = groupOf(a), gb = groupOf(b);
      var wrap = el('div', 'ef-cmpempty');
      var h = el('p', 'ef-cmpempty__head',
        short(A) + ' and ' + short(B) + ' cannot be compared side by side.');
      var why = ga && gb && ga !== gb
        ? el('p', 'ef-cmpempty__why',
            withArticle(noun(ga)).replace(/^./, function (c) { return c.toUpperCase(); }) +
            ' and ' + withArticle(noun(gb)) + ' do not share a spec sheet — ' +
            'there is no row that means the same thing on both.')
        : el('p', 'ef-cmpempty__why',
            'These two do not share enough specification to line up row by row.');
      var fix = el('p', 'ef-cmpempty__fix',
        'Pick two products of the same kind, or see them on their own pages.');
      wrap.appendChild(h); wrap.appendChild(why); wrap.appendChild(fix);
      return wrap;
    }

    var written = {};
    var cat = document.getElementById('ef-catalog');
    if (cat) { try { written = (JSON.parse(cat.textContent).pairs) || {}; } catch (e) { written = {}; } }

    function draw() {
      /* PAIRS is keyed by design id, so translate before looking up. */
      var da = (BY_ID[a.value] && BY_ID[a.value].design_id) || a.value;
      var db = (BY_ID[b.value] && BY_ID[b.value].design_id) || b.value;
      var key = da + '|' + db, alt = db + '|' + da;
      var own = mount.getAttribute('data-cmp-page');
      var isOwnPair = own === key || own === alt;
      if (!isOwnPair && own) {
        var oi = own.split('|').map(resolve);
        isOwnPair = (oi[0] === a.value && oi[1] === b.value)
                 || (oi[0] === b.value && oi[1] === a.value);
      }
      var url = written[key] || written[alt];
      if (url) {
        if (!isOwnPair) { location.href = url; return; }
        /* Already on the written page for this pair. Its designed comparison —
           sections, prose, a considered three-column layout — is directly below,
           and drawing a plain table underneath would say the same numbers twice
           and worse. The generated table exists for the pairs nobody wrote. */
        mount.hidden = true;
        mount.replaceChildren();
        if (mount.hasAttribute('data-cmp-hub') && HEAD) HEAD.textContent = HEAD_TEXT;
        if (EXAMPLE) EXAMPLE.hidden = false;   /* this IS the written pair */
        return;
      }
      var ok = render(mount, BY_ID[a.value], BY_ID[b.value]);
      if (!ok) {
        /* Explain, name the pair actually chosen, and put the written cards
           away. The heading used to be left naming the PREVIOUS working pair,
           so three different comparisons could be on screen at once: the one
           chosen, the one named and the one argued. */
        mount.replaceChildren(emptyState(BY_ID[a.value], BY_ID[b.value]));
        mount.hidden = false;
        if (HEAD) HEAD.textContent = short(BY_ID[a.value]) + ' vs ' + short(BY_ID[b.value]);
        if (EXAMPLE) EXAMPLE.hidden = true;
        return;                 /* no URL: a shared link to nothing is worse
                                   than a comparison that cannot be shared. */
      }
      mount.hidden = false;
      if (EXAMPLE) EXAMPLE.hidden = !isOwnPair;
      /* Put the pair in the URL. This page already READS ?a=&b= on load, so the
         only thing missing was writing it: a shopper who built a comparison had
         a correct page behind an address that described none of it, and could
         not send it to anyone or reopen it on a laptop. That matters here more
         than on most stores — these are RM4,000-31,000 decisions researched
         across several sessions and devices.

         replaceState, not pushState: changing a dropdown is not a navigation,
         and stacking history entries would make Back walk the picker backwards
         instead of leaving the page. */
      if (ok && userPicked && window.history && history.replaceState) {
        var q = new URLSearchParams(location.search);
        q.set('a', a.value); q.set('b', b.value);
        history.replaceState(null, '', location.pathname + '?' + q.toString());
      }
      /* The hub's h1 names the pair its written example is about. Once the
         reader picks a different pair the generated table answers it, and a
         heading still naming the old pair reads as the page having ignored
         them. A written pair page keeps its own h1 — that is a verdict. */
      if (ok && mount.hasAttribute('data-cmp-hub') && HEAD) {
        HEAD.textContent = short(BY_ID[a.value]) + ' vs ' + short(BY_ID[b.value]);
      }
    }

    /* The designed selects list the six products that had written pages. The
       point of this phase is that any two comparable records can be put side by
       side, so the options come from the data — grouped, so the reader is not
       offered a fridge against a solar panel and then told it cannot be done. */
    var GROUP_LABEL = {
      'ac-power-station': 'Power stations',
      'dc-power-station': 'DC power stations',
      'solar-panel': 'Solar panels',
      'portable-air-conditioner': 'Air conditioning',
      'fridge-freezer': 'Fridge freezers',
      'vehicle-charger': 'Vehicle chargers'
    };
    function populate(sel) {
      var keep = sel.value;
      var byGroup = {};
      DATA.products.forEach(function (p) {
        if (!p.compare_enabled) return;          /* held records never appear */
        (byGroup[p.group] = byGroup[p.group] || []).push(p);
      });
      sel.replaceChildren();
      Object.keys(byGroup).forEach(function (g) {
        /* A group with one member can still be chosen, but it has no peer, so
           the table simply will not draw — the picker says so by offering it
           rather than hiding it and leaving the reader wondering. */
        var og = document.createElement('optgroup');
        og.label = GROUP_LABEL[g] || g;
        byGroup[g].forEach(function (p) {
          var o = document.createElement('option');
          o.value = p.id; o.textContent = p.title;
          og.appendChild(o);
        });
        sel.appendChild(og);
      });
      if (keep && BY_ID[keep]) sel.value = keep;
    }
    populate(a);
    populate(b);
    /* A pair page opens on its own pair. Browsers restore select values across a
       reload, so without this the page can come back showing someone else's
       comparison above a verdict written about this one. */
    var own = mount.getAttribute('data-cmp-page');
    if (own) {
      var ids = own.split('|').map(resolve);
      if (ids[0] && ids[1]) { a.value = ids[0]; b.value = ids[1]; }
    }
    /* A pair chosen on the catalogue arrives as ?a=&b=. It wins over the page's
       own default, because the reader picked it and expects to land on it. */
    var q = new URLSearchParams(location.search);
    var qa = resolve(q.get('a')), qb = resolve(q.get('b'));
    if (qa && qb) { a.value = qa; b.value = qb; }
    if (a.value === b.value) {
      /* Two identical selections draw nothing; open on a real comparison. */
      var alt = DATA.products.filter(function (p) {
        return p.compare_enabled && p.group === BY_ID[a.value].group && p.id !== a.value;
      })[0];
      if (alt) b.value = alt.id;
    }

    /* Only a real choice puts the pair in the address bar. Writing it on the
       first draw would give every arrival at /pages/compare a query string it
       did not ask for — noise in analytics, and a second address for a page
       that already has a canonical one. */
    function pick() { userPicked = true; draw(); }
    a.addEventListener('change', pick);
    b.addEventListener('change', pick);
    var swap = scope.querySelector('[data-cmpswap]');
    if (swap) {
      swap.addEventListener('click', function (e) {
        e.preventDefault();
        var av = a.value; a.value = b.value; b.value = av;
        userPicked = true;
        draw();
      });
    }
    draw();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
